<template>
  <component
    :is="lazyLoad ? 'div' : 'CCard'"
    v-if="lazyLoad || (creativeReviews && creativeReviews.length)"
  >
    <CCardHeader v-if="!lazyLoad">
      Creative Reviews
      <CIcon
        v-c-tooltip="'Displays a list of buyer reviewed creatives'"
        name="cil-check-circle"
        size="custom"
      />
    </CCardHeader>
    <component :is="lazyLoad ? 'div' : 'CCardBody'">
      <slot name="form" />
      <div
        v-if="isLoading"
        class="text-center p-5"
      >
        <CSpinner
          style="width:2rem;height:2rem;"
          color="info"
          grow
        />
      </div>
      <div v-else-if="creativeReviews && creativeReviews.length">
        <CDataTable
          data-cy="table-creative-reviews"
          small
          border
          sorter
          column-filter
          :sorter-value="{ column: 'name', asc: true }"
          :items="formattedCreativeReviews"
          :fields="fields"
          :items-per-page="10"
          :pagination="{align: 'center'}"
        >
          <template #id="{item}">
            <td>
              <router-link
                :to="{ name: 'CreativeDetails', params: { id: getSafe(() => item.id)} }"
                target="_blank"
              >
                {{ getSafe(() => item.id) }}
              </router-link>

              <a
                href
                class="ml-1"
                @click.prevent="
                  copyIdToClipboard(getSafe(() => item.id))
                "
              ><CIcon
                name="cil-copy"
              /></a>
            </td>
          </template>
          <template #actions="{item}">
            <td>
              <CButton
                v-if="item.status !== 'Approved'"
                color="success"
                size="sm"
                class="mr-2"
                @click="showApproveModal(item)"
              >
                Approve
              </CButton>
              <CButton
                v-if="item.status !== 'Rejected'"
                color="danger"
                size="sm"
                @click="showRejectModal(item)"
              >
                Reject
              </CButton>
            </td>
          </template>
        </CDataTable>
      </div>
      <div v-else>
        No creative reviews found.
      </div>
    </component>

    <CModal
      title="Confirm approval"
      :show.sync="warningApproveModal"
    >
      Are you sure you want to approve this creative review on behalf of the buyer?

      <CTextarea
        v-model="approveReason"
        class="mt-3"
        label="Reason"
        placeholder="Enter reason for approval"
        horizontal
        rows="5"
        required
        invalid-feedback="Please enter a reason"
      />

      <template #footer>
        <ButtonLoading
          color="success"
          :loading="isActionLoading"
          :disabled="!approveReason"
          @click="approveReview"
        >
          Approve
        </ButtonLoading>

        <CButton
          color="secondary"
          @click="warningApproveModal = false"
        >
          Cancel
        </CButton>
      </template>
    </CModal>

    <CModal
      title="Confirm rejection"
      :show.sync="warningRejectModal"
    >
      Are you sure you want to reject this creative review on behalf of the buyer?

      <CTextarea
        v-model="rejectReason"
        class="mt-3"
        label="Reason"
        placeholder="Enter reason for rejection"
        horizontal
        rows="5"
        required
        invalid-feedback="Please enter a reason"
      />

      <template #footer>
        <ButtonLoading
          color="danger"
          :loading="isActionLoading"
          :disabled="!rejectReason"
          @click="rejectReview"
        >
          Reject
        </ButtonLoading>

        <CButton
          color="secondary"
          @click="warningRejectModal = false"
        >
          Cancel
        </CButton>
      </template>
    </CModal>
  </component>
</template>

<script>
import CreativeApi from '@/api/creative.api'
import ButtonLoading from '@/components/common/ButtonLoading.vue'
import { showSuccessMessage, showErrorMessage } from '@/notification-utils'

export default {
  name: 'BuyerCreatives',

  components: {
    ButtonLoading
  },

  props: {
    accountId: {
      type: String,
      default: ''
    },

    lazyLoad: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      isLoading: false,
      isActionLoading: false,
      creativeReviews: [],
      selectedReview: null,
      approveReason: '',
      rejectReason: '',
      warningApproveModal: false,
      warningRejectModal: false,
      fields: [
        { key: 'id', label: 'ID', sortable: true },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'cmsId', label: 'SMID (CMS Id)', sortable: true },
        { key: 'trafficSourceName', label: 'Traffic Source', sortable: true },
        { key: 'countryId', label: 'Country', sortable: true },
        { key: 'languageId', label: 'Language', sortable: true },
        { key: 'marketCategory', label: 'Type', sortable: true },
        { key: 'categoryName', label: 'Category', sortable: true },
        { key: 'actions', label: 'Actions', sorter: false, filter: false }
      ],
    }
  },

  computed: {
    formattedCreativeReviews() {
      return this.creativeReviews
        .map(review => {
          return {
            ...review,
            creativeReviewId: review.id,
            id: this.getSafe(() => review.creative.id),
            status: review.status,
            categoryName: 'Health - Medicare',
            trafficSourceName: this.getSafe(() => review.creative.trafficSourceName),
            cmsId: this.getSafe(() => review.creative.cmsId),
            countryId: this.getSafe(() => review.creative.countryCode),
            languageId: this.getSafe(() => review.creative.languageCode),
            marketCategory: this.getSafe(() => review.creative.marketCategory),
          }
        })
    },
  },

  async created () {
    if (!this.lazyLoad) {
      await this.loadCreatives()
    }
  },

  methods: {

    async loadCreatives() {
      try {
        this.isLoading = true
        const response = await CreativeApi.getBuyerCreatives(this.accountId)
        this.creativeReviews = response.data
      } catch (error) {
        //
      } finally {
        this.isLoading = false
      }
    },

    showApproveModal(item) {
      this.selectedReview = item
      this.approveReason = ''
      this.warningApproveModal = true
    },

    showRejectModal(item) {
      this.selectedReview = item
      this.rejectReason = ''
      this.warningRejectModal = true
    },

    async approveReview() {
      try {
        this.isActionLoading = true
        await CreativeApi.reviewBuyerCreative(this.accountId, {
          creativeReviewId: this.selectedReview.creativeReviewId,
          isApproved: true,
          reason: this.approveReason
        })
        showSuccessMessage('Creative review approved successfully')
        await this.loadCreatives()
      } catch (error) {
        showErrorMessage('Failed to approve creative review')
      } finally {
        this.isActionLoading = false
        this.warningApproveModal = false
        this.selectedReview = null
        this.approveReason = ''
      }
    },

    async rejectReview() {
      try {
        this.isActionLoading = true
        await CreativeApi.reviewBuyerCreative(this.accountId, {
          creativeReviewId: this.selectedReview.creativeReviewId,
          isApproved: false,
          reason: this.rejectReason
        })
        showSuccessMessage('Creative review rejected successfully')
        await this.loadCreatives()
      } catch (error) {
        showErrorMessage('Failed to reject creative review')
      } finally {
        this.isActionLoading = false
        this.warningRejectModal = false
        this.selectedReview = null
        this.rejectReason = ''
      }
    }
  },
}
</script>

<style>

</style>
